/**
 * Threshold judgement for a measured signature (PF-6119).
 *
 * **Byte-identical twin of
 * `physiofit_cloud_functions/functions/utils/signatureRecapture/judgeSignatureInk.ts`. Change one and
 * you must change the other** — a divergence here is worse than a divergence in measurement, because
 * it is silent on both sides: the button would enable on a signature the server then rejects, with
 * nothing the patient can act on.
 *
 * Pure: metrics in, verdict out, no measurement and no I/O. That is what lets the browser and the
 * `sharp` server measurement reach the same conclusion, and what makes this module a cheap lift into
 * Cloud Functions when the app needs it too.
 *
 * Deliberately NOT a judgement about whether the signature is genuine or legible — only whether
 * enough ink spans enough of the pad to be a signature at all.
 */
import type {
  SignatureInkCheck,
  SignatureInkMetrics,
  SignatureInkThresholds,
  SignatureInkVerdict,
} from '@physiofit-digital/physiofit_types';

/**
 * Applies the configured minimums to a measured signature.
 *
 * @param {SignatureInkMetrics} metrics Measured ink.
 * @param {SignatureInkThresholds} thresholds Configured minimums.
 * @return {SignatureInkVerdict} Verdict plus the checks that failed.
 */
export const judgeInk = (
  metrics: SignatureInkMetrics,
  thresholds: SignatureInkThresholds,
): SignatureInkVerdict => {
  const failedChecks: SignatureInkCheck[] = [];
  if (metrics.inkPixels < thresholds.minInkPixels) failedChecks.push('minInkPixels');
  if (metrics.inkRatio < thresholds.minInkRatio) failedChecks.push('minInkRatio');
  if (metrics.boundingBoxWidthRatio < thresholds.minBoundingBoxWidthRatio) {
    failedChecks.push('minBoundingBoxWidthRatio');
  }
  if (metrics.boundingBoxHeightRatio < thresholds.minBoundingBoxHeightRatio) {
    failedChecks.push('minBoundingBoxHeightRatio');
  }
  return failedChecks.length === 0 ? { accepted: true, metrics } : { accepted: false, metrics, failedChecks };
};
